import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  Dialog,
  Button,
  Typography,
  Tooltip,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import { ImageMetadata } from '../../types/ui';
import dayjs from 'dayjs';
import { DialogTitle, Checkbox } from '@mui/material';
import useMediaLabels from '../MainComponent/hooks/useMediaLabels';
import CustomTypography from '../../ui/atoms/CustomTypography';
import { IconCheck } from '../../assets/icons/IconCheck';
import { useDispatch, useSelector } from 'react-redux';
import {
  addMediaLabelToImage,
  deleteMediaLabel,
  moveMediasFromLabel,
  removeAdditionalLabel,
} from '../../redux/reducer/coreScriptReducer';
import useImages from '../MainComponent/hooks/useImages';
import useQueryObject from '../../hooks/useQueryObject';
import IconRemove from '../../assets/icons/IconRemove';
import IconMove from '../../assets/icons/IconMove';
import IconCopy from '../../assets/icons/IconCopy';
import {
  downloadFileFromUrl,
  downloadMultipleFilesAsZip,
  sameFileExtension,
} from '../../utils';
import ImageZoomViewer from '../../ui/molecules/zoomable-image';
import CustomTooltip from '../../ui/atoms/CustomTooltip';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import { RootState, useAppSelector } from '../../redux/Store';
import { contextValue, DateFormat } from '../../constants';

const formatFileSize = (bytes: number | undefined): string => {
  if (!bytes) return 'N/A';
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};

type ImageDialogProps = {
  open: boolean;
  onClose: () => void;
  type?: 'default' | 'addtionallabel' | 'aiMedia';
  initialIndex?: number;
  images: ImageMetadata[];
};

const metaDataList: { key: string; title: string }[] = [
  { title: 'File Name', key: 'imageFileName' },
  { title: 'Uploaded Date', key: 'dateCreated' },
  { title: 'Image Taken Date', key: 'imageTakenDate' },
  { title: 'Camera Make', key: 'cameraMake' },
  { title: 'Camera Model', key: 'cameraModel' },
  { title: 'Camera OS Version', key: 'cameraOsVersion' },
  { title: 'Orientation', key: '' },
  { title: 'Software', key: 'cameraOsVersion' },
  { title: 'Pixel X Dimension', key: 'imageWidth' },
  { title: 'Pixel Y Dimension', key: 'imageHeight' },
  { title: 'Image Size', key: 'fileSize' },
  { title: 'Assigned Label', key: 'label' },
  { title: 'GPS', key: 'gps' },
];

const ImageDialog = ({
  open,
  onClose,
  type = 'default',
  initialIndex = -1,
  images,
}: ImageDialogProps) => {
  const dispatch = useDispatch();
  const { useImage403ErrorHandler } = useImages();
  const contextId = useAppSelector((state) => state.coreScript.contextId);
  const { processImageCloneRequest } = useImages();
  const { cloneAllowed } = useQueryObject();
  const [index, setIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const imgRef = useRef<HTMLImageElement>(null);

  const { isReadOnly, queryObj } = useSelector(
    (state: RootState) => state.contextId
  );

  const image = useMemo(() => {
    return images[index];
  }, [images, index]);

  const [copyMoveLabel, setCopyMoveLabel] = useState<
    'copy' | 'move' | 'attach' | undefined
  >();

  const handlePrev = useCallback(() => {
    if (index > 0) {
      setIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : 0));
      setLoading(true);
    }
  }, [index]);

  const handleNext = useCallback(() => {
    if (index < images.length - 1) {
      setIndex((prevIndex) => prevIndex + 1);
      setLoading(true);
    }
  }, [images.length, index]);

  useEffect(() => {
    const handleKeyDown = (event: any) => {
      if ((event.key === 'ArrowLeft' || event.key === 'ArrowUp') && index > 0) {
        handlePrev();
      } else if (
        (event.key === 'ArrowRight' || event.key === 'ArrowDown') &&
        index < images.length - 1
      ) {
        handleNext();
      }
    };

    globalThis.addEventListener('keydown', handleKeyDown);
    return () => {
      globalThis.removeEventListener('keydown', handleKeyDown);
    };
  }, [index, images.length, handlePrev, handleNext]);

  const handleThumbnailClick = (idx: number) => {
    setIndex(idx);
    setLoading(true);
  };

  const handleSaveAs = async () => {
    await downloadFileFromUrl(image.webFileName, image.imageFileName);
  };

  const handleDownloadAllImages = async () => {
    const files = images.map((e) => {
      return {
        url: e.webFileName,
        fileName: sameFileExtension(e.webFileName, e.guid),
      };
    });
    const value = dayjs()
      ?.format(DateFormat.DATE_TIME_YYYY_MM_DD_HH_MM_a)
      .replaceAll(' ', '_');
    downloadMultipleFilesAsZip(files, `${value}-${contextId}.zip`);
  };

  useEffect(() => {
    if (!open) return;
    if (initialIndex >= 0 && images.at(initialIndex) && open) {
      setIndex(initialIndex);
    } else {
      setIndex(0);
    }
  }, [initialIndex, images, open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          height: '100vh',
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <DialogActions
        sx={{
          background: '#000',
          padding: '8px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant='body1'
          sx={{
            color: '#FFF',
            fontWeight: 600,
            fontSize: '16px',
            flexGrow: 1,
            textAlign: 'left',
          }}
        >
          {`${image?.prefix ? image?.prefix + ':' : ''}  ${image?.description ? image?.description?.toLanguagePack() : image?.imageFileName}`}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CustomTooltip label='Download All'>
            <IconButton
              onClick={handleDownloadAllImages}
              sx={{
                '&.Mui-disabled': {
                  cursor: 'not-allowed',
                  pointerEvents: 'all !important', // Override pointer-events: none
                },
              }}
            >
              <DownloadForOfflineIcon
                sx={{ color: '#fff', width: 16, height: 16 }}
              />
            </IconButton>
          </CustomTooltip>
          <Tooltip title='Save As'>
            <IconButton
              onClick={handleSaveAs}
              color='primary'
              sx={{
                '&.Mui-disabled': {
                  cursor: 'not-allowed',
                  pointerEvents: 'all !important', // Override pointer-events: none
                },
              }}
            >
              <DownloadIcon sx={{ color: '#fff', width: 16, height: 16 }} />
            </IconButton>
          </Tooltip>
          {queryObj?.context !== contextValue?.AuditResultsBySubmissionId && (
            <>
              {image?.label && type === 'default' && (
                <Tooltip title='Move'>
                  <IconButton
                    color='primary'
                    onClick={() => setCopyMoveLabel('move')}
                    sx={{
                      '&.Mui-disabled': {
                        cursor: 'not-allowed',
                        pointerEvents: 'all !important', // Override pointer-events: none
                      },
                    }}
                  >
                    <IconMove color='white' />
                  </IconButton>
                </Tooltip>
              )}

              {cloneAllowed && type === 'default' && (
                <Tooltip title={image?.label ? 'Clone' : 'Attach Label'}>
                  <IconButton
                    color='primary'
                    onClick={() => {
                      if (isReadOnly) return false;
                      setCopyMoveLabel(image?.label ? 'copy' : 'attach');
                    }}
                    disabled={isReadOnly}
                    sx={{
                      '&.Mui-disabled': {
                        cursor: 'not-allowed',
                        pointerEvents: 'all !important', // Override pointer-events: none
                      },
                    }}
                  >
                    <IconCopy color='white' />
                  </IconButton>
                </Tooltip>
              )}

              {image?.label && (
                <Tooltip title='Remove'>
                  <IconButton
                    color='primary'
                    onClick={() => {
                      if (isReadOnly) return false;
                      if (type) {
                        dispatch(
                          removeAdditionalLabel({
                            label: image?.label,
                            guids: [image.guid],
                          })
                        );
                      } else {
                        dispatch(
                          deleteMediaLabel({
                            label: image?.label,
                            guids: [image.guid],
                          })
                        );
                      }
                    }}
                    disabled={isReadOnly}
                    sx={{
                      '&.Mui-disabled': {
                        cursor: 'not-allowed',
                        pointerEvents: 'all !important', // Override pointer-events: none
                      },
                    }}
                  >
                    <IconRemove color='white' />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
          <Tooltip title='Close'>
            <IconButton onClick={onClose} color='primary'>
              <CloseIcon sx={{ color: '#fff' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogActions>

      {/* Content */}
      <DialogContent
        sx={{
          background: '#111',
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          height: '100%',
          padding: 0,
        }}
      >
        {/* Left Section (Image & Thumbnails) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '70%',
            background: 'rgba(0, 0, 0, 0.6)',
          }}
        >
          {/* Image */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flex: 1,
              gap: '2rem',
            }}
          >
            <Button
              onClick={handlePrev}
              disabled={index === 0}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#FFF',
                minWidth: '40px',
              }}
            >
              <ArrowBackIosNewIcon />
            </Button>

            <Box
              sx={{
                flex: 1,
                height: '70vh',
              }}
            >
              <ImageZoomViewer
                imageSrc={image?.webFileName}
                loading={loading}
                setLoading={setLoading}
                onLoad={() => setLoading(false)}
              />
            </Box>
            <Button
              onClick={handleNext}
              disabled={index >= images.length - 1}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#FFF',
                minWidth: '40px',
              }}
            >
              <ArrowForwardIosIcon />
            </Button>
          </Box>

          {/* Thumbnails */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              padding: '8px',
              overflowX: 'auto',
              background: '#111',
              marginTop: '2vh',
              height: '20vh',
            }}
          >
            {images.map((img, idx) => (
              <img
                ref={imgRef}
                key={idx}
                src={`${img?.webFileName}?time=${new Date()}` || ''}
                alt={`Thumbnail ${idx + 1}`}
                onClick={() => handleThumbnailClick(idx)}
                style={{
                  width: '100px',
                  height: '100px',
                  objectFit: 'cover',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  border:
                    index === idx ? '2px solid #FFF' : '2px solid transparent',
                }}
                onError={useImage403ErrorHandler}
              />
            ))}
          </Box>
        </Box>

        {/* Metadata Section */}
        <Box
          sx={{
            width: '30%',
            padding: '16px',
            color: '#DDD',
            background: '#000',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {metaDataList.map((item, idx) => (
              <MetaDataItem
                key={idx}
                metadata={image}
                title={item.title || ''}
                itemKey={item.key || ''}
              />
            ))}
          </Box>
        </Box>
        <DialogComponent
          type={copyMoveLabel}
          label={image?.label || ''}
          guid={image?.guid || ''}
          onClose={() => setCopyMoveLabel(undefined)}
          onSubmit={(targetLabel: string[]) => {
            setCopyMoveLabel(undefined);
            onClose();

            for (const tgLabel of targetLabel) {
              if (copyMoveLabel === 'copy') {
                processImageCloneRequest(tgLabel, [image?.guid]);
              } else if (copyMoveLabel === 'attach') {
                const media = [
                  {
                    Guid: image.guid,
                    ThumbUri: image.webFileName,
                  },
                ];
                dispatch(
                  addMediaLabelToImage({
                    label: tgLabel,
                    media: media,
                  })
                );
              } else {
                if (image?.label) {
                  dispatch(
                    moveMediasFromLabel({
                      fromLabel: image?.label,
                      toLabel: tgLabel,
                      guids: [image?.guid],
                    })
                  );
                } else {
                  console.log('current label is required');
                }
              }
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

type MetaDataItemProp = {
  title: string;
  itemKey: string;
  metadata: ImageMetadata;
};

const MetaDataItem = ({ title, itemKey, metadata }: MetaDataItemProp) => {
  const renderGPSLink = () => {
    const { latitude, longitude } = metadata || {};
    if (latitude && longitude) {
      return (
        <a
          target='_blank'
          rel='noopener noreferrer'
          href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
          style={{
            color: '#00aaff',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            textDecoration: 'none',
            fontSize: '16px',
            fontFamily: 'Arial, sans-serif',
            padding: '5px',
            borderRadius: '3px',
            transition: 'color 0.3s ease, background-color 0.3s ease',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.color = '#0088cc';
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.color = '#00aaff';
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
          }}
        >
          {`${latitude}, ${longitude}`}
        </a>
      );
    }
    return 'N/A';
  };

  const getValue = () => {
    if (itemKey === 'gps') {
      return renderGPSLink();
    }

    const result =
      metadata && itemKey && metadata[itemKey] ? metadata[itemKey] : 'N/A';
    if (itemKey === 'imageFileName') {
      return metadata.guid
        ? sameFileExtension(metadata.webFileName, metadata.guid)
        : metadata.imageFileName;
    }
    if (itemKey === 'label') {
      return result?.toLanguagePack();
    }
    if (itemKey === 'fileSize') {
      return formatFileSize(Number(result));
    }
    if (itemKey === 'imageTakenDate' || (itemKey === 'dateCreated' && result)) {
      const date = dayjs(result).format('MM/DD/YYYY - hh:mm A');
      return date;
    }

    return result;
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        padding: '0 10px 5px',
      }}
    >
      <Typography variant='body2' style={{ flex: 1 }}>
        <strong>{title}</strong>
      </Typography>
      <Typography variant='body2' style={{ textAlign: 'end' }}>
        {getValue()}
      </Typography>
    </div>
  );
};

type DialogComponentProps = {
  type: 'copy' | 'move' | 'attach' | undefined;
  label: string;
  guid: string;
  onClose: () => void;
  onSubmit: (selected: string[]) => void;
};

const DialogComponent = ({
  type,
  label,
  onClose,
  onSubmit,
}: DialogComponentProps) => {
  const { labels, mediaList } = useMediaLabels();

  const filteredLabels = useMemo(() => {
    return labels.filter((item) => item !== label);
  }, [labels, label]);

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter labels based on the search query (case-insensitive)
  const searchedLabels = useMemo(() => {
    const lowercasedQuery = searchQuery.toLowerCase()?.toLanguagePack();
    return filteredLabels.filter((item) =>
      item.toLowerCase()?.toLanguagePack().includes(lowercasedQuery)
    );
  }, [filteredLabels, searchQuery]);

  useEffect(() => {
    if (!type) {
      setSelectedOptions([]);
    }
  }, [type]);

  const handleSelectValue = (id: string) => {
    if (type === 'copy' || type === 'attach') {
      setSelectedOptions((prev) =>
        prev.includes(id)
          ? prev.filter((option) => option !== id)
          : [...prev, id]
      );
    } else {
      setSelectedOptions([id]); // Replace selected options in single-select mode
    }
  };

  const handleSubmit = () => {
    onClose();
    onSubmit(selectedOptions);
    setSelectedOptions([]);
  };

  return (
    <Dialog
      open={type === 'copy' || type === 'move' || type === 'attach'}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
      PaperProps={{
        style: {
          height: '85vh', // Set dialog height to 85% of the viewport
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {type === 'copy'
          ? `Select Labels (${filteredLabels.length})`
          : 'Select Label'}
        <DialogActions>
          <Button
            variant='contained'
            sx={{
              display: 'flex',
              height: '42px',
              width: '200px',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: '8px',
              borderRadius: '48px',
              border: '1px solid #E3E3E3',
              background: '#1C6EC9',
              color: '#FFF',
              fontFamily: 'Montserrat',
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: '500',
              lineHeight: '28px',
            }}
            onClick={handleSubmit}
            disabled={selectedOptions?.length === 0}
          >
            {type?.toUpperCase()}
          </Button>
          <IconButton
            aria-label='close'
            onClick={onClose}
            sx={{
              color: '#343330',
              padding: 0,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogActions>
      </DialogTitle>
      <DialogContent>
        {/* Search Bar */}
        <TextField
          fullWidth
          variant='outlined'
          placeholder='Search labels...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ marginBottom: '16px' }}
        />
        {/* Render filtered and searched labels */}
        {searchedLabels.map((label, index) => (
          <ItemLabel
            key={index}
            type={type}
            label={label}
            mediaList={mediaList}
            selectedOptions={selectedOptions}
            handleSelectValue={handleSelectValue}
          />
        ))}
      </DialogContent>
    </Dialog>
  );
};

const ItemLabel = ({
  label,
  mediaList,
  selectedOptions,
  handleSelectValue,
}: any) => {
  const details = useMemo(() => {
    return mediaList[label];
  }, [label, mediaList]);

  const isResolved = Number(details?.Have) >= Number(details?.Min);

  return (
    <div
      key={label}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 0',
        cursor: 'pointer',
      }}
      onClick={(e) => {
        handleSelectValue(label);
        e.stopPropagation();
      }}
    >
      <Checkbox
        checked={selectedOptions.includes(label)}
        inputProps={{
          'aria-label': `checkbox for ${label}`,
        }}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <Typography noWrap title={label.toLanguagePack()}>
          {label.toLanguagePack()}
        </Typography>
      </div>
      <CustomTypography
        variant='body2'
        sx={{
          fontFamily: 'Montserrat',
          fontSize: '10px',
          fontWeight: 400,
          lineHeight: '12.19px',
          textAlign: 'left',
          textUnderlinePosition: 'from-font',
          textDecorationSkipInk: 'none',
        }}
      >
        {isResolved ? (
          <Box sx={{ alignItems: 'center' }}>
            <IconCheck />
          </Box>
        ) : (
          <Box>
            {Number(details?.Have)}/{Number(details?.Min)}
            {details.Required && (
              <span
                style={{
                  color: 'red',
                  fontSize: '13px',
                  marginLeft: '4px',
                }}
              >
                *
              </span>
            )}
          </Box>
        )}
      </CustomTypography>
    </div>
  );
};

export default ImageDialog;
